export const UI_LOG_SCHEMA_VERSION = 1 as const;
export const UI_LOG_SOURCE = 'navigator_ui' as const;

// These event names are part of the POST /v1/log request schema. Preserve
// compatibility with the server-side allowlist during staged rollout.
export const APPROVED_UI_EVENT_NAMES = [
  'authentication_succeeded',
  'recording_started',
  'recording_stopped',
  'uncaught_error',
  'unhandled_rejection',
  'react_error_boundary',
] as const;

export type UiEventName = typeof APPROVED_UI_EVENT_NAMES[number];
export type UiKeyEventName = Extract<
  UiEventName,
  'authentication_succeeded' | 'recording_started' | 'recording_stopped'
>;
export type UiErrorEventName = Extract<
  UiEventName,
  'uncaught_error' | 'unhandled_rejection' | 'react_error_boundary'
>;
export type UiLogSeverity = 'info' | 'error';

export interface UiEventContext {
  readonly schemaVersion: typeof UI_LOG_SCHEMA_VERSION;
  readonly source: typeof UI_LOG_SOURCE;
  readonly eventName: UiEventName;
  readonly occurredAt: string;
  readonly stage: string;
  readonly region: string;
  readonly componentStack?: string;
}

const ACTION_TYPE_TO_EVENT_NAME: Readonly<Record<string, UiKeyEventName>> = {
  'recording/startRecording': 'recording_started',
  'recording/recordingStopped': 'recording_stopped',
};

const AUTHENTICATION_SUCCEEDED_ACTION_TYPE = 'user/getUser/fulfilled';

const ERROR_EVENT_NAMES: readonly UiEventName[] = [
  'uncaught_error',
  'unhandled_rejection',
  'react_error_boundary',
];

const MAX_ERROR_NAME_LENGTH = 64;
const MAX_STACK_LENGTH = 4000;
const MAX_COMPONENT_STACK_LENGTH = 2000;
const MAX_STACK_FRAMES = 20;
const URL_PATTERN = /\b(?:https?|wss?):\/\/[^\s"'<>)}\]]+/gi;
const JWT_PATTERN =
  /\b[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g;
const SECRET_ASSIGNMENT_PATTERN =
  /\b(authorization|cookie|password|passwd|token|secret)\b["'\s]*[:=]\s*(?:"[^"]*"|'[^']*'|(?:bearer\s+)?[^\s,;}]+)/gi;

const valueToErrorName = (value: unknown): string => {
  if (!(value instanceof Error)) {
    return 'UnknownError';
  }

  const candidate = value.name.slice(0, MAX_ERROR_NAME_LENGTH);
  return /^[A-Za-z][A-Za-z0-9_.-]*$/.test(candidate)
    ? candidate
    : 'UnknownError';
};

// Webpack names production scripts [name].[contenthash:8].js and
// [name].[contenthash:8].chunk.js (configuration/configUtils.js). A stack frame
// on one of them keeps only that file name and line:column, which identify
// Navigator's own code and nothing about the user, so a crash can be decoded
// with the build's source maps. The origin and path are dropped.
const SCRIPT_LOCATION_PATTERN =
  /^https?:\/\/[^\s/]+\/(?:[^\s?#]*\/)?([A-Za-z0-9_.~-]+\.[0-9a-f]{8}(?:\.chunk)?\.js)((?::\d+){0,2})$/;

const REDACTED_URL = '[REDACTED_URL]';

export const urlToScriptLocation = (url: string): string | null => {
  const match = SCRIPT_LOCATION_PATTERN.exec(url);
  return match ? `${match[1]}${match[2]}` : null;
};

const urlReplacementToRedactedText =
  (urlToReplacement: (url: string) => string) =>
  (value: string): string =>
    value
      .replace(SECRET_ASSIGNMENT_PATTERN, '$1=[REDACTED]')
      .replace(JWT_PATTERN, '[REDACTED_TOKEN]')
      .replace(URL_PATTERN, urlToReplacement);

export const valueToRedactedText = urlReplacementToRedactedText(
  () => REDACTED_URL,
);

// Stack text keeps Navigator script locations; every other URL is redacted.
export const valueToRedactedStackText = urlReplacementToRedactedText(
  (url) => urlToScriptLocation(url) ?? REDACTED_URL,
);

// Chrome and Edge start error.stack with a "Name: message" title, which can
// span several lines, and write frames as "    at fn (url:line:col)". Firefox
// and Safari write no title and frames as "fn@url:line:col". Keeping only
// frame lines drops the message in both formats without dropping Firefox's
// first frame, where the error was thrown. The pattern is chosen per stack so
// a Chrome title that happens to look like a Firefox frame is still dropped.
const V8_FRAME_PATTERN = /^\s+at\s/;
const AT_SIGN_FRAME_PATTERN = /@.*:\d+:\d+$/;

const valueToStackFrames = (value: unknown): string => {
  if (!(value instanceof Error) || !value.stack) {
    return '';
  }

  const lines = value.stack.split('\n');
  const framePattern = lines.some((line) => V8_FRAME_PATTERN.test(line))
    ? V8_FRAME_PATTERN
    : AT_SIGN_FRAME_PATTERN;

  return lines
    .filter((line) => framePattern.test(line))
    .slice(0, MAX_STACK_FRAMES)
    .join('\n');
};

export const valuesToSanitizedError = (
  eventName: UiErrorEventName,
  value: unknown,
): Error => {
  const errorName = valueToErrorName(value);
  const safeError = new Error(eventName);
  safeError.name = errorName;

  const stackFrames = valueToRedactedStackText(valueToStackFrames(value))
    .slice(0, MAX_STACK_LENGTH);
  safeError.stack = stackFrames
    ? `${errorName}: ${eventName}\n${stackFrames}`
    : `${errorName}: ${eventName}`;

  return safeError;
};

export const valueToSanitizedComponentStack = (
  value: string | null | undefined,
): string | undefined => {
  if (!value) {
    return undefined;
  }

  const redactedValue = valueToRedactedStackText(value)
    .slice(0, MAX_COMPONENT_STACK_LENGTH);
  return redactedValue || undefined;
};

export const actionToUiEventName = (
  action: unknown,
): UiKeyEventName | null => {
  if (
    typeof action !== 'object'
    || action === null
    || !('type' in action)
    || typeof action.type !== 'string'
  ) {
    return null;
  }

  if (action.type === AUTHENTICATION_SUCCEEDED_ACTION_TYPE) {
    const payload = 'payload' in action ? action.payload : null;
    return (
      typeof payload === 'object'
      && payload !== null
      && 'token' in payload
      && typeof payload.token === 'string'
      && payload.token.length > 0
    )
      ? 'authentication_succeeded'
      : null;
  }

  return ACTION_TYPE_TO_EVENT_NAME[action.type] ?? null;
};

export const uiEventNameToSeverity = (
  eventName: UiEventName,
): UiLogSeverity =>
  ERROR_EVENT_NAMES.includes(eventName) ? 'error' : 'info';

export const uiEventNameToSampleRate = (_eventName: UiEventName): number => 1;

export const valuesToUiEventContext =
  (stage: string) =>
  (region: string) =>
  (occurredAt: string) =>
  (eventName: UiEventName): UiEventContext => ({
    schemaVersion: UI_LOG_SCHEMA_VERSION,
    source: UI_LOG_SOURCE,
    eventName,
    occurredAt,
    stage,
    region,
  });

export const approvedUiEventName = (value: string): value is UiEventName =>
  APPROVED_UI_EVENT_NAMES.includes(value as UiEventName);
